import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import __sortBy from 'lodash/sortBy';
import __filter from 'lodash/filter';
import __orderBy from 'lodash/orderBy';

import { getActiveClassrooms, getArchivedClassrooms } from '../../services/classroom';
import { syncClassrooms, isCleverTeacher } from '../../services/teacher';
import { notify } from '../../utils/functions';
import { trackClassroomCreated } from '../../utils/analytics';
import { setClassrooms } from '../../redux/actions';

import './Classrooms.scss';

import Card from '../../components/global/Card';
import DropdownSelect from '../../components/global/DropdownSelect';

class Classrooms extends Component {
  constructor(props) {
    super(props);

    this.state = {
      classrooms: [],
      sortOptions: [
        { value: 'created', text: 'Created At' },
        { value: 'name', text: 'Name' },
        { value: 'numStudents', text: props.vocabulary["dashboard.student.textUpperPlural"] },
      ],
      teacherInfo: JSON.parse(sessionStorage.getItem('teacherData')),
      filter: ''
    };

    this.sortByFilter = this.sortByFilter.bind(this);
    this.filterClassrooms = this.filterClassrooms.bind(this);
  }

  async componentDidMount() {
    const teacherInfo = this.state.teacherInfo;
    if (!this.props.siteContent.isPartner) {
      const sortOptions = this.state.sortOptions;
      sortOptions.push({ value: 'grades[0]', text: 'Grade Level' });
      this.setState({ sortOptions: sortOptions });
    }

    // show what we already have right away; this is also the "before sync" snapshot
    const classroomsBefore = await this.fetchActiveClassroomsSafely(teacherInfo?.id);
    if (!isCleverTeacher(teacherInfo)) {
      return;
    }

    const syncResults = await this.syncCleverClassrooms();

    // Refetch even if sync reported an error: a sync that timed out (503) can
    // still have created classrooms, and the Parse SDK's automatic retry of
    // that 503 then sees them as already existing.
    const classroomsAfter = await this.fetchActiveClassroomsSafely(teacherInfo?.id);

    if (classroomsBefore && classroomsAfter) {
      this.trackSyncedClassrooms(classroomsBefore, classroomsAfter, syncResults);
    }
  }

  async fetchActiveClassroomsSafely(teacherId) {
    try {
      return await this.fetchActiveClassrooms(teacherId);
    } catch (error) {
      console.error('[ClassroomSync] fetching classrooms failed', error);
      return null;
    }
  }

  async syncCleverClassrooms() {
    try {
      return await syncClassrooms();
    } catch (error) {
      console.error('[ClassroomSync] sync failed', error);
      notify('error', `Error: ${error}`, 5000);
      return null;
    }
  }

  /**
   * Sends the classroom created event for each Clever classroom that's in the
   * list after sync but wasn't before. Compares our own classroom lists rather
   * than trusting the SyncClassrooms response, which may come from a retry.
   */
  trackSyncedClassrooms(classroomsBefore, classroomsAfter, syncResults) {
    const idsBefore = new Set(classroomsBefore.map((classroom) => classroom.id));
    const created = classroomsAfter.filter((classroom) => classroom.cleverId && !idsBefore.has(classroom.id));

    // prefer the Clever roster size; fall back to the saved classroom's roster
    const sections = syncResults?.sections || [];
    created.forEach((classroom) => {
      const section = sections.find((s) => s.data?.id === classroom.cleverId);

      trackClassroomCreated({
        teacherInfo: this.state.teacherInfo,
        classroomId: classroom.id,
        classrooms: classroomsAfter,
        studentCountAtCreation: section?.data?.students?.length ?? classroom.studentCount,
      });
    });
  }

  componentDidUpdate(prevProps) {
    const { classrooms } = this.state;
    if (prevProps.classrooms === null) {
      this.setState({ classrooms: classrooms })
    }
  }

  async fetchActiveClassrooms(teacherId) {
    const classrooms = await getActiveClassrooms({ teacherId });
    // convert all classroom names to lowercase so that we can sort correctly
    const formattedClassrooms = this.convertNamesToLC(classrooms);
    // sort by latest created (same as sortByFilter with 'Created At')
    const sortedClassrooms = __orderBy(formattedClassrooms, ['created'], ['desc']);
    this.setState({
      classrooms: sortedClassrooms,
      filter: 'active',
    });
    return sortedClassrooms;
  }

  // convert all classroom names to lowercase so that we can sort correctly
  convertNamesToLC(classrooms) {
    const modifiedClassrooms = classrooms.map((classroom) => {
      const filter = classroom;
      filter.name = classroom.name.toLowerCase();
      return filter;
    });
    return modifiedClassrooms;
  }

  filterClassrooms(status) {
    if (status === 'archived') {
      getArchivedClassrooms({ teacherId: this.state.teacherInfo.id }).then((classrooms) => {
        const formattedClassrooms = this.convertNamesToLC(classrooms);
        const sortedClassrooms = __orderBy(formattedClassrooms, ['created'], ['desc']);
        this.setState({
          classrooms: sortedClassrooms,
          filter: status
        });
      });
    } else {
      this.fetchActiveClassrooms(this.state.teacherInfo.id);
    }
  }

  sortByFilter(selected) {
    if (selected.text === 'Grade Level'){
      const sortedClassrooms = this.sortByGrade();
      this.setState({ classrooms: sortedClassrooms });
    } else if (selected.text === 'Created At') {
      this.setState({ classrooms: __orderBy(this.state.classrooms, [selected.value], ["desc"]) });
    } else {
      this.setState({ classrooms: __sortBy(this.state.classrooms, [selected.value]) });
    }
  }

  sortByGrade() {
    // filter K classes out first then sort based on grade and then name
    const classroomsWk = __filter(this.state.classrooms, (c) => c.grades[0] === 'K');
    const classroomsWOk = __filter(this.state.classrooms, (c) => c.grades[0] !== 'K');

    const sortedKClassrooms = __sortBy(classroomsWk, ['name']);
    const sortedNonKClassrooms = __sortBy(classroomsWOk, [function (room) {
      return room.grades[0];
    }, function (room) {
      return room.name;
    }]);

    return sortedKClassrooms.concat(sortedNonKClassrooms);
  }

  render() {
    let addCard;
    const { vocabulary } = this.props;
    const { filter, classrooms, teacherInfo } = this.state;

    if (filter !== 'archived') {
      if (teacherInfo?.isOauthTeacher) {
        // todo: have this check for specific type of oauth user
        addCard = (<li>
          <Card
            text="Please use Clever to add classrooms"
            vocabulary={vocabulary}
            to=""
          />
        </li>);
      } else {
        addCard = (<li>
          <Card
            type="add"
            to="/dashboard/classrooms/create"
            text={`add ${vocabulary["dashboard.classroom.text"]}`}
            vocabulary={vocabulary}
          />
        </li>);
      }
    }

    return (
      <div className="classrooms-container page">
        <Helmet>
          <title>Classrooms List | codeSpark Teacher Dashboard</title>
        </Helmet>
        <div className="classrooms-subnav">
          <div className="sort">
            sort by
            <DropdownSelect
              type="select"
              options={this.state.sortOptions}
              onSelect={this.sortByFilter}
            />
          </div>
          <ul>
            <li className={filter !== 'archived' ? 'active' : ''}>
              <button onClick={() => this.filterClassrooms('all')}>Active {vocabulary["dashboard.classroom.textUpperPlural"]}</button>
            </li>
            <li className={filter === 'archived' ? 'active' : ''}>
              <button onClick={() => this.filterClassrooms('archived')} >Archived {vocabulary["dashboard.classroom.textUpperPlural"]}</button>
            </li>
          </ul>
        </div>
        <ul className="cards">
          {
            classrooms !== null &&
            classrooms.map(room => (
              <li key={room.id}>
                <Card
                  type="classroom"
                  to={`classroom/${room.id}`}
                  linkParams={{ id: room.id }}
                  text={room.name}
                  teacher=""
                  gradeLevels={room.grades}
                  vocabulary={vocabulary}
                />
              </li>
            ))
          }
          {addCard}
        </ul>
      </div>
    );
  }
}

export default Classrooms;
